
import { AnyAction, Reducer, ReducersMapObject } from 'redux';

import { mapObject } from '@utils/mapObject';

type BaseReducer<T extends object> = Reducer<T>;
type NestedReducerResult<T extends object, S extends ReducersMapObject> = Omit<T, keyof S> & {
  [K in keyof S]: ReturnType<S[K]>;
};

/**
 * Combines a base reducer and an object of nested sub reducers to return a state slice
 * with all fields of the base reducer at the top level, and nested state subsections indexed
 * according to the given reducer map. This helper can be used to create a reducer for a feature
 * that nests sub-features inside of the main state model to reduce the bulk of the base reducer.
 *
 * Example input interfaces:
 *
 *  interface IBaseReducer {
 *    a: string;
 *    b: number;
 *  }
 *
 *  interface ISubReducer {
 *    c: string;
 *  }
 *
 * Output type of combineNestedReducers(baseReducer, { sub: subReducer }):
 *
 * {
 *   a: string;
 *   b: number:
 *   sub: {
 *     c: string;
 *   };
 * }
 */
export const combineNestedReducers = <T extends object, S extends ReducersMapObject>(
  baseReducer: BaseReducer<T>,
  nestedReducers: S,
): Reducer<NestedReducerResult<T, S>> => {
  return (state, action) => {
    const nestedReducerResults = mapObject(
      nestedReducers,
      (reducerFn, section) => reducerFn(state?.[section], action),
    );

    return {
      ...baseReducer(state, action),
      ...nestedReducerResults,
    };
  };
};
